import { type ElementType, type FC, type PropsWithChildren } from 'react';

import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { type MicroCopy } from '@vakantiesnl/types';

import useStyles from './RatingBox.style';
import { getRatingConfig } from './utils';

export interface RatingBoxProps {
	size?: 'xSmall' | 'small' | 'medium' | 'large' | 'jumbo';
	rating: number | undefined;
	label?: boolean;
	component?: ElementType;
	className?: string;
	labelClassName?: string;
	microCopies?: MicroCopy;
}

export const RatingBox: FC<PropsWithChildren<RatingBoxProps>> = ({
	rating,
	label = true,
	size = 'medium',
	children,
	component = 'div',
	className,
	labelClassName,
	microCopies,
}) => {
	const { classes: styles, cx } = useStyles();
	const ratingConfig = getRatingConfig(styles, microCopies, rating);

	const Component = component;
	const ratingLabel = label && ratingConfig.label;

	const sizeClass = {
		[styles.xSmall]: size === 'xSmall',
		[styles.small]: size === 'small',
		[styles.medium]: size === 'medium',
		[styles.large]: size === 'large',
		[styles.jumbo]: size === 'jumbo',
	};

	return (
		<Component className={cx(styles.container, className)} data-qa="rating">
			<span className={cx(ratingConfig.className, styles.rating, sizeClass, 'rating')} data-qa="score">
				<Icon className={styles.shapeIcon} symbol={ratingConfig.shapeIcon} />
				<span className={styles.ratingValue} data-cy="rating-score">
					{ratingConfig.rating}
				</span>
			</span>
			{(children || label) && (
				<span className={cx(styles.wrapper, sizeClass)}>
					{ratingLabel && (
						<span className={cx(ratingConfig.className, styles.label, sizeClass, labelClassName)}>
							{ratingLabel}
						</span>
					)}
					{children}
				</span>
			)}
		</Component>
	);
};
